import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '@/views/HomeView.vue'
import { useAuthStore } from '@/stores/auth'

const campaignBuilderView = () => import('@/views/CampaignBuilderView.vue')

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', name: 'home', component: HomeView, meta: { requiresAuth: true } },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { guestOnly: true },
    },
    {
      path: '/invitations/:token',
      name: 'accept-invite',
      component: () => import('@/views/AcceptInviteView.vue'),
    },
    {
      path: '/linkedin-accounts',
      name: 'linkedin-accounts',
      component: () => import('@/views/LinkedinAccountsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/linkedin-accounts/connect',
      name: 'connect-linkedin',
      component: () => import('@/views/ConnectLinkedinAccountView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/linkedin-accounts/:accountId/import',
      name: 'linkedin-account-import',
      component: () => import('@/views/LinkedinAccountImportView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/linkedin-accounts/:accountId/voice-profile',
      name: 'voice-profile',
      component: () => import('@/views/VoiceProfileView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/relationships',
      name: 'contacts',
      component: () => import('@/views/ContactsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/contacts/:contactId',
      name: 'contact-detail',
      component: () => import('@/views/ContactDetailView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/contacts/:contactId/edit',
      name: 'contact-edit',
      component: () => import('@/views/ContactEditView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/integrations',
      name: 'integrations',
      component: () => import('@/views/IntegrationsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/campaigns',
      name: 'campaigns',
      component: () => import('@/views/CampaignsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/campaigns/new',
      name: 'new-campaign',
      component: () => import('@/views/NewCampaignView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/inbox',
      name: 'inbox',
      component: () => import('@/views/InboxView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/inbox/:campaignContactId',
      name: 'inbox-thread',
      component: () => import('@/views/InboxThreadView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/campaigns/:campaignId',
      name: 'campaign-builder',
      redirect: (to) => ({ name: 'campaign-builder-overview', params: to.params }),
      meta: { requiresAuth: true },
    },
    {
      path: '/campaigns/:campaignId/overview',
      name: 'campaign-builder-overview',
      component: campaignBuilderView,
      meta: { requiresAuth: true, builderStep: 1 },
    },
    {
      path: '/campaigns/:campaignId/audience',
      name: 'campaign-builder-audience',
      component: campaignBuilderView,
      meta: { requiresAuth: true, builderStep: 2 },
    },
    {
      path: '/campaigns/:campaignId/sequence',
      name: 'campaign-builder-sequence',
      component: campaignBuilderView,
      meta: { requiresAuth: true, builderStep: 3 },
    },
    {
      path: '/campaigns/:campaignId/branch-rules',
      name: 'campaign-builder-branch-rules',
      component: campaignBuilderView,
      meta: { requiresAuth: true, builderStep: 4 },
    },
    {
      path: '/signals',
      name: 'signals',
      component: () => import('@/views/SignalsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/admin/ai-usage',
      name: 'admin-ai-usage',
      component: () => import('@/views/admin/AiUsageLogListView.vue'),
      meta: { requiresAuth: true, requiresSuperadmin: true },
    },
    {
      path: '/admin/ai-usage/:id',
      name: 'admin-ai-usage-show',
      component: () => import('@/views/admin/AiUsageLogDetailView.vue'),
      meta: { requiresAuth: true, requiresSuperadmin: true },
    },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (!auth.bootstrapped) {
    await auth.bootstrap()
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.meta.guestOnly && auth.isAuthenticated) {
    return { name: 'home' }
  }

  if (to.meta.requiresSuperadmin && !auth.isSuperadmin) {
    return { name: 'home' }
  }

  return true
})

export default router